import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Minus, Plus, AlertCircle, ExternalLink, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { themeVarsFromHex } from "@/lib/theme";
import { SocialMediaIcons } from "@/components/SocialMediaIcons";
import { LogoStack } from "@/components/LogoStack";
import { PlacesAutocompleteInput } from "@/components/maps/PlacesAutocompleteInput";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPicker } from "@/components/maps/MapPicker";
import { MapPreview } from "@/components/maps/MapPreview";
import { phoneToE164 } from "@/lib/phone-utils";
import { sendOwnerPush } from "@/lib/owner-push";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

type Step = "menu" | "details" | "confirm" | "success";
type Fulfillment = "pickup" | "delivery";

type PublicMenuResponse = {
  business_id: string;
  business_name: string;
  business_slug: string;
  business_logo_url: string | null;
  business_primary_color: string | null;
  business_description?: string | null;
  business_order_policy?: string | null;
  business_terms_and_conditions?: string | null;
  business_phone?: string | null;
  whatsapp_url?: string | null;
  instagram_url?: string | null;
  facebook_url?: string | null;
  twitter_url?: string | null;
  linkedin_url?: string | null;
  tiktok_url?: string | null;
  location_address?: string | null;
  location_place_id?: string | null;
  location_lat?: number | null;
  location_lng?: number | null;
  currency: string | null;
  items: Array<{
    id: string;
    name: string;
    description: string | null;
    price_amount: number;
    currency: string | null;
    category_id: string | null;
    image_url: string | null;
    media_items?: any;
  }>;
  categories: Array<{ id: string; name: string }>;
  orders_multiple_menus_enabled?: boolean;
  menus?: Array<{ id: string; name: string; slug: string; is_default?: boolean }> | null;
};

type ServiceAddon = {
  id: string;
  name: string;
  price_amount: number;
  max_quantity?: number | null;
};

type MediaItem = { url: string; type: "image" | "video" };

const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReducedMotion(!!mq.matches);
    onChange();
    // Safari fallback
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    // eslint-disable-next-line deprecation/deprecation
    mq.addListener(onChange);
    // eslint-disable-next-line deprecation/deprecation
    return () => mq.removeListener(onChange);
  }, []);
  return prefersReducedMotion;
};

const normalizeMediaItems = (raw: any, opts?: { cap?: number; dedupe?: boolean }): MediaItem[] => {
  const cap = Math.max(1, Number(opts?.cap ?? 6));
  const dedupe = opts?.dedupe !== false;
  const seen = new Set<string>();
  const out: MediaItem[] = [];

  if (!Array.isArray(raw)) return out;

  for (const it of raw) {
    const url = String(it?.url || "").trim();
    if (!url) continue;

    const tRaw = String(it?.type || "").toLowerCase();
    const type: MediaItem["type"] =
      tRaw === "video" || tRaw === "image"
        ? (tRaw as any)
        : (() => {
            const lower = url.toLowerCase();
            return lower.endsWith(".mp4") ||
              lower.endsWith(".webm") ||
              lower.endsWith(".mov") ||
              lower.endsWith(".avi") ||
              lower.endsWith(".mkv")
              ? "video"
              : "image";
          })();

    const key = `${type}:${url}`;
    if (dedupe && seen.has(key)) continue;
    seen.add(key);

    out.push({ url, type });
    if (out.length >= cap) break;
  }

  return out;
};

function mapsUrl(args: { placeId?: string | null; address?: string | null; lat?: any; lng?: any }) {
  const placeId = args.placeId ? String(args.placeId) : "";
  if (placeId) return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
  const lat = args.lat != null ? Number(args.lat) : null;
  const lng = args.lng != null ? Number(args.lng) : null;
  if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }
  const address = args.address ? String(args.address) : "";
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return "";
}

function money(currency: string, amount: number) {
  return `${currency} ${Number(amount || 0).toFixed(2)}`;
}

export default function FoodOrderPage() {
  const { businessSlug, menuSlug } = useParams();
  const [step, setStep] = useState<Step>("menu");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [data, setData] = useState<PublicMenuResponse | null>(null);
  const [extraSocialLinks, setExtraSocialLinks] = useState<any[]>([]);
  const [extraLogos, setExtraLogos] = useState<any[]>([]);
  const [bookingHeaderMediaRaw, setBookingHeaderMediaRaw] = useState<any>(null);
  const [cart, setCart] = useState<
    Record<string, { quantity: number; notes: string; addons?: Record<string, number> }>
  >({});

  const [addonsByServiceId, setAddonsByServiceId] = useState<Record<string, ServiceAddon[]>>({});
  const [addonsLoadingByServiceId, setAddonsLoadingByServiceId] = useState<Record<string, boolean>>({});
  const [itemSheetOpen, setItemSheetOpen] = useState(false);
  const [itemSheetServiceId, setItemSheetServiceId] = useState<string | null>(null);
  const [draftQty, setDraftQty] = useState<number>(1);
  const [draftNotes, setDraftNotes] = useState<string>("");
  const [draftAddonQtyById, setDraftAddonQtyById] = useState<Record<string, number>>({});
  const [quickAddLoadingById, setQuickAddLoadingById] = useState<Record<string, boolean>>({});

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [fulfillment, setFulfillment] = useState<Fulfillment>("delivery");
  const [address, setAddress] = useState("");
  const [addressPlaceId, setAddressPlaceId] = useState("");
  const [addressLat, setAddressLat] = useState<number | null>(null);
  const [addressLng, setAddressLng] = useState<number | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);
  const [notes, setNotes] = useState("");

  // Draft state for the map picker dialog (prevents the dialog from closing while typing/selecting)
  const [pickerAddress, setPickerAddress] = useState("");
  const [pickerPlaceId, setPickerPlaceId] = useState("");
  const [pickerLat, setPickerLat] = useState<number | null>(null);
  const [pickerLng, setPickerLng] = useState<number | null>(null);

  useEffect(() => {
    if (!locationOpen) return;
    // Seed the dialog draft state from the current form values
    setPickerAddress(address || "");
    setPickerPlaceId(addressPlaceId || "");
    setPickerLat(addressLat);
    setPickerLng(addressLng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationOpen]);

  const [submitting, setSubmitting] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);

  const resetToStart = () => {
    setStep("menu");
    setCart({});
    setNotes("");
    setConfirmationCode(null);
    setSubmitting(false);
    setLocationOpen(false);
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const run = async () => {
      if (!businessSlug) return;
      setLoading(true);
      setError(null);
      try {
        const { data: res, error: rpcErr } = await (supabase as any).rpc("get_public_menu_items", {
          _business_slug: businessSlug,
          _menu_slug: menuSlug || null,
        });
        if (rpcErr) throw rpcErr;
        const row = (res && res[0]) || null;
        if (!row) throw new Error("Business not found");
        setData(row as any);
      } catch (e: any) {
        setData(null);
        setError(e?.message || "Failed to load menu");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [businessSlug, menuSlug]);

  // Load additional business branding (extra socials + extra logos)
  useEffect(() => {
    const businessId = (data as any)?.business_id ? String((data as any).business_id) : "";
    if (!businessId) return;
    let cancelled = false;
    (async () => {
      try {
        const [linksRes, logosRes] = await Promise.all([
          supabase
            .from("business_social_links" as any)
            .select("id, platform, label, url, position, active, is_primary")
            .eq("business_id", businessId)
            .eq("active", true)
            .order("platform", { ascending: true })
            .order("is_primary", { ascending: false })
            .order("position", { ascending: true }),
          supabase
            .from("business_logos" as any)
            .select("id, label, url, position, active")
            .eq("business_id", businessId)
            .eq("active", true)
            .order("position", { ascending: true }),
        ]);
        if (cancelled) return;
        setExtraSocialLinks((linksRes as any)?.data || []);
        setExtraLogos((logosRes as any)?.data || []);
      } catch {
        if (cancelled) return;
        setExtraSocialLinks([]);
        setExtraLogos([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [(data as any)?.business_id]);

  // Load booking header media (reused from appointments booking page hero)
  useEffect(() => {
    const businessId = (data as any)?.business_id ? String((data as any).business_id) : "";
    if (!businessId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: biz, error: bizErr } = await supabase
          .from("businesses" as any)
          .select("booking_header_media_items")
          .eq("id", businessId)
          .maybeSingle();
        if (cancelled) return;
        if (bizErr) throw bizErr;
        setBookingHeaderMediaRaw((biz as any)?.booking_header_media_items ?? null);
      } catch {
        if (cancelled) return;
        setBookingHeaderMediaRaw(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [(data as any)?.business_id]);

  const extraAccountsFor = (platform: string) => {
    const key = String(platform || "").trim().toLowerCase();
    const rows = (extraSocialLinks || []).filter(
      (r: any) => String(r.platform || "").trim().toLowerCase() === key,
    );
    return rows
      .filter((r: any) => !r?.is_primary)
      .map((r: any) => ({ url: String(r.url || ""), label: String(r.label || "").trim() || "Other" }));
  };

  const primaryLabelFor = (platform: string) => {
    const key = String(platform || "").trim().toLowerCase();
    const row = (extraSocialLinks || []).find(
      (r: any) => String(r.platform || "").trim().toLowerCase() === key && !!r?.is_primary,
    );
    return String(row?.label || "Main").trim() || "Main";
  };

  const currency = (data?.currency as string) || "GHS";
  const themeStyle = data?.business_primary_color ? themeVarsFromHex(String(data.business_primary_color)) : undefined;
  const prefersReducedMotion = usePrefersReducedMotion();

  const items = (data?.items || []) as PublicMenuResponse["items"];
  const mediaForItem = (it: any): MediaItem | null => {
    const fromList = normalizeMediaItems((it as any)?.media_items, { cap: 6, dedupe: true });
    if (fromList.length > 0) return fromList[0] || null;
    const url = String((it as any)?.image_url || "").trim();
    if (!url) return null;
    const lower = url.toLowerCase();
    const type: MediaItem["type"] =
      lower.endsWith(".mp4") ||
      lower.endsWith(".webm") ||
      lower.endsWith(".mov") ||
      lower.endsWith(".avi") ||
      lower.endsWith(".mkv")
        ? "video"
        : "image";
    return { url, type };
  };

  const categories = (data?.categories || []) as any[];
  const businessDescription = (data as any)?.business_description ? String((data as any).business_description) : "";
  const businessOrderPolicy = (data as any)?.business_order_policy ? String((data as any).business_order_policy) : "";
  const businessTerms = (data as any)?.business_terms_and_conditions ? String((data as any).business_terms_and_conditions) : "";

  const [selectedCategory, setSelectedCategory] = useState<"all" | "uncategorized" | string>("all");
  const displayItems = useMemo(() => {
    if (selectedCategory === "all") return items;
    if (selectedCategory === "uncategorized") return items.filter((i) => !i.category_id);
    return items.filter((i) => String(i.category_id || "") === String(selectedCategory));
  }, [items, selectedCategory]);

  const heroMediaItems = useMemo(() => {
    // Prefer explicit booking header media configured by the business (same as appointments page).
    if (Array.isArray(bookingHeaderMediaRaw) && bookingHeaderMediaRaw.length > 0) {
      return normalizeMediaItems(bookingHeaderMediaRaw, { cap: 6, dedupe: true });
    }

    // Fallback to menu item media (keeps header useful if no header media configured)
    const derived: MediaItem[] = [];
    const seen = new Set<string>();
    for (const it of items || []) {
      const fromList = normalizeMediaItems((it as any)?.media_items, { cap: 6, dedupe: true });
      const candidate = (fromList[0] as any) || (() => {
        const url = String((it as any)?.image_url || "").trim();
        if (!url) return null;
        const lower = url.toLowerCase();
        const type: MediaItem["type"] =
          lower.endsWith(".mp4") ||
          lower.endsWith(".webm") ||
          lower.endsWith(".mov") ||
          lower.endsWith(".avi") ||
          lower.endsWith(".mkv")
            ? "video"
            : "image";
        return { url, type };
      })();
      if (!candidate?.url) continue;
      const key = `${candidate.type}:${candidate.url}`;
      if (seen.has(key)) continue;
      seen.add(key);
      derived.push(candidate);
      if (derived.length >= 6) break;
    }
    const images = derived.filter((i) => i.type === "image");
    const videos = derived.filter((i) => i.type === "video");
    return images.length > 0 ? images : videos;
  }, [items, bookingHeaderMediaRaw]);

  const [heroIndex, setHeroIndex] = useState(0);
  useEffect(() => {
    setHeroIndex(0);
  }, [(data as any)?.business_id]);

  const heroCurrent = heroMediaItems[heroIndex] || null;
  const heroHasMultiple = heroMediaItems.length > 1;
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (heroCurrent?.type === "video" && heroCurrent?.url) {
      const el = heroVideoRef.current;
      if (el) {
        const p = el.play();
        if (p?.catch) p.catch(() => {});
      }
    }
  }, [heroIndex, heroCurrent?.type, heroCurrent?.url]);

  useEffect(() => {
    if (step !== "menu") return;
    if (prefersReducedMotion) return;
    if (!heroHasMultiple) return;
    if (heroCurrent?.type === "video") return; // advance videos via onEnded
    const id = window.setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroMediaItems.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, [step, prefersReducedMotion, heroHasMultiple, heroCurrent?.type, heroMediaItems.length]);

  const cartLines = useMemo(() => {
    const map = cart || {};
    const out: Array<{
      item: (typeof items)[number];
      quantity: number;
      notes: string;
      addons: Record<string, number>;
    }> = [];
    for (const it of items) {
      const v = map[it.id];
      if (!v || !v.quantity) continue;
      out.push({
        item: it,
        quantity: Math.max(1, Number(v.quantity || 1)),
        notes: (v.notes || "").trim(),
        addons: (v.addons || {}) as Record<string, number>,
      });
    }
    return out;
  }, [cart, items]);

  const cartCount = useMemo(() => {
    const vals = Object.values(cart || {});
    return vals.reduce((s, v: any) => s + Math.max(0, Number(v?.quantity || 0)), 0);
  }, [cart]);

  const addonUnitTotalFor = (serviceId: string, addonQtyById: Record<string, number>) => {
    const addons = addonsByServiceId[serviceId] || [];
    if (!addons || addons.length === 0) return 0;
    return addons.reduce((sum, a) => {
      const q = Math.max(0, Number(addonQtyById[a.id] || 0));
      if (!q) return sum;
      return sum + (Number(a.price_amount || 0) * q);
    }, 0);
  };

  const total = useMemo(() => {
    return cartLines.reduce((sum, l) => {
      const base = Number(l.item.price_amount || 0);
      const addonsUnit = addonUnitTotalFor(l.item.id, l.addons || {});
      return sum + (base + addonsUnit) * l.quantity;
    }, 0);
  }, [cartLines, addonsByServiceId]);

  const canContinueFromMenu = cartLines.length > 0;
  const needsDeliveryAddress = fulfillment === "delivery";
  const canSubmit =
    name.trim().length > 0 &&
    phone.trim().length > 0 &&
    cartLines.length > 0 &&
    (!needsDeliveryAddress || address.trim().length > 0);
  const fulfillmentLabel = fulfillment === "delivery" ? "Delivery" : "Pickup";

  // If user switches to pickup, clear delivery fields.
  useEffect(() => {
    if (fulfillment !== "pickup") return;
    setAddress("");
    setAddressPlaceId("");
    setAddressLat(null);
    setAddressLng(null);
    setLocationOpen(false);
  }, [fulfillment]);

  const setQty = (id: string, qty: number) => {
    setCart((prev) => {
      const next = { ...(prev || {}) };
      if (qty <= 0) {
        delete next[id];
        return next;
      }
      const current = next[id] || { quantity: 0, notes: "", addons: {} as Record<string, number> };
      next[id] = { ...current, quantity: qty };
      return next;
    });
  };

  const loadAddonsForService = async (sid: string) => {
    if (!sid) return [] as ServiceAddon[];
    if (addonsByServiceId[sid]) return addonsByServiceId[sid] || [];
    setAddonsLoadingByServiceId((p) => ({ ...p, [sid]: true }));
    try {
      const { data: rows, error: aErr } = await supabase
        .from("service_addons" as any)
        .select("id,name,price_amount,max_quantity")
        .eq("service_id", sid)
        .eq("active", true)
        .order("created_at", { ascending: true });
      if (aErr) throw aErr;
      const cleaned = (rows || []) as any;
      setAddonsByServiceId((p) => ({ ...p, [sid]: cleaned }));
      return cleaned;
    } catch {
      setAddonsByServiceId((p) => ({ ...p, [sid]: [] }));
      return [];
    } finally {
      setAddonsLoadingByServiceId((p) => ({ ...p, [sid]: false }));
    }
  };

  const openItemSheet = (serviceId: string) => {
    setItemSheetServiceId(serviceId);
    setItemSheetOpen(true);
  };

  useEffect(() => {
    const sid = itemSheetServiceId;
    if (!itemSheetOpen || !sid) return;
    // Seed draft from cart
    const cur = cart[sid];
    setDraftQty(Math.max(1, Math.floor(Number(cur?.quantity || 1))));
    setDraftNotes(String(cur?.notes || ""));
    setDraftAddonQtyById({ ...((cur?.addons || {}) as Record<string, number>) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSheetOpen, itemSheetServiceId]);

  // Lazy-load add-ons for the open item sheet
  useEffect(() => {
    const sid = itemSheetServiceId;
    if (!itemSheetOpen || !sid) return;
    if (addonsByServiceId[sid]) return;
    let cancelled = false;
    (async () => {
      try {
        setAddonsLoadingByServiceId((p) => ({ ...p, [sid]: true }));
        const { data: rows, error: aErr } = await supabase
          .from("service_addons" as any)
          .select("id,name,price_amount,max_quantity")
          .eq("service_id", sid)
          .eq("active", true)
          .order("created_at", { ascending: true });
        if (aErr) throw aErr;
        if (cancelled) return;
        setAddonsByServiceId((p) => ({ ...p, [sid]: (rows || []) as any }));
      } catch {
        if (cancelled) return;
        setAddonsByServiceId((p) => ({ ...p, [sid]: [] }));
      } finally {
        if (cancelled) return;
        setAddonsLoadingByServiceId((p) => ({ ...p, [sid]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemSheetOpen, itemSheetServiceId, addonsByServiceId]);

  const setDraftAddonQty = (addonId: string, qty: number) => {
    setDraftAddonQtyById((prev) => {
      const next = { ...(prev || {}) };
      const clamped = Math.max(0, Math.floor(Number(qty || 0)));
      if (clamped <= 0) delete next[addonId];
      else next[addonId] = clamped;
      return next;
    });
  };

  const applyItemSheet = () => {
    const sid = itemSheetServiceId;
    if (!sid) return;
    const q = Math.max(0, Math.floor(Number(draftQty || 0)));
    const cleanedAddons: Record<string, number> = {};
    Object.entries(draftAddonQtyById || {}).forEach(([k, v]) => {
      const n = Math.max(0, Math.floor(Number(v || 0)));
      if (n > 0) cleanedAddons[String(k)] = n;
    });
    setCart((prev) => {
      const next = { ...(prev || {}) };
      if (q <= 0) {
        delete next[sid];
        return next;
      }
      const cur = next[sid] || { quantity: 1, notes: "", addons: {} as Record<string, number> };
      next[sid] = {
        ...cur,
        quantity: q,
        notes: String(draftNotes || ""),
        addons: cleanedAddons,
      };
      return next;
    });
    setItemSheetOpen(false);
    setItemSheetServiceId(null);
  };

  const quickAdd = async (serviceId: string) => {
    if (!serviceId) return;
    if (quickAddLoadingById[serviceId]) return;
    setQuickAddLoadingById((p) => ({ ...p, [serviceId]: true }));
    try {
      const addons = await loadAddonsForService(serviceId);
      if (Array.isArray(addons) && addons.length > 0) {
        openItemSheet(serviceId);
        return;
      }
      setCart((prev) => {
        const next = { ...(prev || {}) };
        const cur = next[serviceId] || { quantity: 0, notes: "", addons: {} as Record<string, number> };
        next[serviceId] = { ...cur, quantity: Math.max(0, Number(cur.quantity || 0)) + 1 };
        return next;
      });
    } finally {
      setQuickAddLoadingById((p) => ({ ...p, [serviceId]: false }));
    }
  };

  const submit = async () => {
    if (!businessSlug || !data) return;
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const cleanedPhone = phoneToE164(phone.trim()) || phone.trim();
      const payloadItems = cartLines.map((l) => ({
        service_id: l.item.id,
        quantity: l.quantity,
        notes: l.notes || null,
        addons: Object.entries(l.addons || {})
          .filter(([, q]) => Number(q || 0) > 0)
          .map(([addon_id, quantity]) => ({
            addon_id,
            quantity: Math.max(1, Math.floor(Number(quantity || 1))),
          })),
      }));
      const { data: res, error: rpcErr } = await (supabase as any).rpc("submit_food_order", {
        _business_slug: businessSlug,
        _customer_name: name.trim(),
        _customer_phone: cleanedPhone,
        _customer_email: email.trim() || null,
        _delivery_address: fulfillment === "delivery" ? address.trim() || null : null,
        _delivery_place_id: fulfillment === "delivery" ? addressPlaceId.trim() || null : null,
        _delivery_lat: fulfillment === "delivery" && typeof addressLat === "number" ? addressLat : null,
        _delivery_lng: fulfillment === "delivery" && typeof addressLng === "number" ? addressLng : null,
        _items: payloadItems,
        _notes: notes.trim() || null,
      });
      if (rpcErr) throw rpcErr;
      const row = (res && res[0]) || null;
      const orderId = row?.order_id ? String(row.order_id) : "";
      const code = row?.confirmation_code ? String(row.confirmation_code) : "";
      setConfirmationCode(code || "OK");
      setStep("success");

      void sendOwnerPush({
        businessId: data?.business_id,
        title: "New food order",
        body: `A new food order${code ? ` (#${code})` : ""} was placed.`,
        url: "/app/orders",
        data: {
          context: "food_order_created",
          context_id: orderId || null,
          confirmation_code: code || null,
        },
      });

      // Best-effort notifications (email + SMS) for both customer and business.
      // Do not block the success UI.
      void (async () => {
        try {
          if (orderId) {
            await supabase.functions.invoke("send-email", {
              body: { type: "food_order_created", orderId, confirmationCode: code || "" },
            } as any);
          }
        } catch {
          // ignore (email can be configured later)
        }
      })();
    } catch (e: any) {
      // Friendly error for vendors who haven't enabled orders yet (or haven't applied migration)
      const msg = String(e?.message || "Failed to submit order.");
      const lower = msg.toLowerCase();
      if (lower.includes("order limit reached")) {
        alert(
          "This vendor has reached their order limit for the current billing period. Please try again later or contact the business.",
        );
      } else {
        alert(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const focusedServiceId = itemSheetServiceId;
  const focusedItem = focusedServiceId ? items.find((i) => String(i.id) === String(focusedServiceId)) : null;
  const focusedAddons = focusedServiceId ? addonsByServiceId[focusedServiceId] || [] : [];
  const draftAddonUnitTotal =
    focusedServiceId ? addonUnitTotalFor(focusedServiceId, draftAddonQtyById || {}) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background overflow-x-hidden">
        <header className="border-b border-border bg-card">
          <div className="container-app py-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">Back</span>
              </Link>
              <div className="h-4 w-px bg-border" />
              <h1 className="font-semibold text-foreground">Order Food</h1>
            </div>
          </div>
        </header>
        <main className="container-app py-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading menu...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background overflow-x-hidden">
        <header className="border-b border-border bg-card">
          <div className="container-app py-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">Back</span>
              </Link>
              <div className="h-4 w-px bg-border" />
              <h1 className="font-semibold text-foreground">Order Food</h1>
            </div>
          </div>
        </header>
        <main className="container-app py-12">
          <div className="flex flex-col items-center justify-center gap-4 text-center max-w-md mx-auto">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold">Menu Not Available</h2>
            <p className="text-muted-foreground">{error || "This business is not available right now."}</p>
            <Button asChild>
              <Link to="/">Go Home</Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const steps: Array<{ key: Step; label: string }> = [
    { key: "menu", label: "Menu" },
    { key: "details", label: "Details" },
    { key: "confirm", label: "Confirm" },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden" style={themeStyle}>
      <header className="border-b border-border bg-card">
        <div className="container-app py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">Back</span>
              </Link>
              <div className="h-4 w-px bg-border" />
              <h1 className="font-semibold text-foreground">Order Food</h1>
            </div>
            <SocialMediaIcons
              facebookUrl={(data as any)?.facebook_url}
              instagramUrl={(data as any)?.instagram_url}
              mainLabels={{
                instagram: primaryLabelFor("instagram"),
                facebook: primaryLabelFor("facebook"),
                twitter: primaryLabelFor("twitter"),
                linkedin: primaryLabelFor("linkedin"),
                tiktok: primaryLabelFor("tiktok"),
                whatsapp: primaryLabelFor("whatsapp"),
              }}
              twitterUrl={(data as any)?.twitter_url}
              linkedinUrl={(data as any)?.linkedin_url}
              tiktokUrl={(data as any)?.tiktok_url}
              whatsappUrl={(data as any)?.whatsapp_url}
              facebookUrls={extraAccountsFor("facebook")}
              instagramUrls={extraAccountsFor("instagram")}
              twitterUrls={extraAccountsFor("twitter")}
              linkedinUrls={extraAccountsFor("linkedin")}
              tiktokUrls={extraAccountsFor("tiktok")}
              whatsappUrls={extraAccountsFor("whatsapp")}
              size="sm"
              className="flex"
            />
          </div>
        </div>
      </header>

      <main
        className={cn(
          "container-app",
          step === "menu" ? "pt-0 pb-8" : "py-8",
        )}
      >
        {/* Business header */}
          {step === "menu" ? (
            <div className="mb-5 sm:mb-6">
              {/* Full-bleed hero (edge-to-edge on mobile; avoids 100vw overflow quirks in iOS/PWA) */}
              <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
                <div className="relative bg-muted h-64 sm:h-80 md:h-96 overflow-hidden">
                  {heroCurrent ? (
                    heroCurrent.type === "video" ? (
                      <video
                        ref={heroVideoRef}
                        src={heroCurrent.url}
                        className="absolute left-1/2 top-1/2 min-h-full min-w-full w-[115%] h-[115%] -translate-x-1/2 -translate-y-1/2 object-cover object-center"
                        muted
                        playsInline
                        autoPlay
                        loop={!heroHasMultiple}
                        preload="auto"
                        onEnded={() => {
                          if (!heroHasMultiple) return;
                          setHeroIndex((prev) => (prev + 1) % heroMediaItems.length);
                        }}
                        onError={() => {
                          if (!heroHasMultiple) return;
                          setHeroIndex((prev) => (prev + 1) % heroMediaItems.length);
                        }}
                      />
                    ) : (
                      <img
                        src={heroCurrent.url}
                        alt=""
                        aria-hidden="true"
                        loading="eager"
                        className="absolute left-1/2 top-1/2 min-h-full min-w-full w-[115%] h-[115%] -translate-x-1/2 -translate-y-1/2 object-cover object-center"
                      />
                    )
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
                  )}

                  {/* Contrast overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />

                  {/* Content */}
                  <div className="absolute inset-0 px-5 sm:px-8 pb-10 sm:pb-12 flex flex-col justify-end">
                    <div className="flex items-end justify-between gap-4">
                      <div className="min-w-0 max-w-[44rem]">
                        <div
                          className={cn(
                            "flex items-center",
                            String(data.business_logo_url || "").trim() ||
                              (extraLogos || []).some((l: any) => String(l?.url || "").trim())
                              ? "gap-3"
                              : "gap-0",
                          )}
                        >
                          {String(data.business_logo_url || "").trim() ||
                          (extraLogos || []).some((l: any) => String(l?.url || "").trim()) ? (
                            <div className="shrink-0">
                              <LogoStack
                                primaryLogoUrl={String(data.business_logo_url || "")}
                                primaryLabel={`${data.business_name} logo`}
                                fallbackText={String(data.business_name || "B").substring(0, 2).toUpperCase()}
                                logos={(extraLogos || []).map((l: any) => ({
                                  url: String(l.url || ""),
                                  label: String(l.label || ""),
                                }))}
                                size={44}
                                collapsedStep={18}
                                expandedGap={10}
                              />
                            </div>
                          ) : null}
                          <div className="min-w-0">
                            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                              {data.business_name}
                            </h2>
                            <p className="text-white/85 mt-1 text-sm sm:text-base">
                              {businessDescription.trim().length > 0
                                ? businessDescription
                                : "Select items and place your order."}
                            </p>
                          </div>
                        </div>
                      </div>

                      {heroHasMultiple ? (
                        <div className="hidden sm:flex items-center gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="bg-white/20 hover:bg-white/30 text-white border-white/20"
                            onClick={() =>
                              setHeroIndex((prev) => (prev - 1 + heroMediaItems.length) % heroMediaItems.length)
                            }
                            aria-label="Previous photo"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="bg-white/20 hover:bg-white/30 text-white border-white/20"
                            onClick={() => setHeroIndex((prev) => (prev + 1) % heroMediaItems.length)}
                            aria-label="Next photo"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Carousel indicator */}
                  {heroHasMultiple ? (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                      {heroMediaItems.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setHeroIndex(i)}
                          className={cn(
                            "h-1 rounded-full transition-all",
                            i === heroIndex ? "w-4 bg-white/90" : "w-2 bg-white/45 hover:bg-white/65",
                          )}
                          aria-label={`Go to photo ${i + 1}`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          {/* Item bottom-sheet (Bolt-like) */}
          <Dialog
            open={itemSheetOpen}
            onOpenChange={(open) => {
              setItemSheetOpen(open);
              if (!open) setItemSheetServiceId(null);
            }}
          >
            <DialogContent className="p-0 gap-0 w-[100vw] sm:w-full sm:max-w-lg top-auto bottom-0 translate-y-0 rounded-b-none rounded-t-2xl max-h-[90vh] overflow-hidden">
              <div className="flex flex-col max-h-[90vh]">
                <div className="flex-1 overflow-y-auto">
                  {focusedItem ? (
                    <div>
                      <div className="relative bg-muted">
                        {(() => {
                          const m = mediaForItem(focusedItem);
                          if (!m?.url) {
                            return <div className="h-40 w-full bg-gradient-to-br from-primary/10 via-background to-background" />;
                          }
                          if (m.type === "video") {
                            return (
                              <video
                                src={m.url}
                                className="w-full h-56 object-cover"
                                muted
                                playsInline
                                autoPlay
                                loop
                                preload="auto"
                              />
                            );
                          }
                          return (
                            <img
                              src={m.url}
                              alt={String(focusedItem.name || "Menu item")}
                              className="w-full h-56 object-cover"
                              loading="eager"
                            />
                          );
                        })()}
                      </div>

                      <div className="p-4 space-y-3">
                        <div className="space-y-1">
                          <DialogHeader className="space-y-1 text-left">
                            <DialogTitle className="text-xl">{String(focusedItem.name || "Item")}</DialogTitle>
                            <DialogDescription className="text-sm">
                              {focusedItem.description
                                ? String(focusedItem.description)
                                : "Customize your item and add it to your basket."}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="text-base font-semibold text-foreground">
                            {money(focusedItem.currency || currency, Number(focusedItem.price_amount || 0))}
                          </div>
                        </div>

                        {/* Extras */}
                        <div className="space-y-2">
                          <div className="text-sm font-semibold text-foreground">Extras</div>
                          {(focusedServiceId && (addonsLoadingByServiceId[focusedServiceId] || false)) ? (
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading extras...
                            </div>
                          ) : focusedAddons.length === 0 ? (
                            <div className="text-sm text-muted-foreground">No extras for this item.</div>
                          ) : (
                            <div className="space-y-2">
                              {focusedAddons.map((a) => {
                                const qty = Math.max(0, Number((draftAddonQtyById || {})[a.id] || 0));
                                const maxQty = Math.max(1, Number((a as any)?.max_quantity ?? 1));
                                const isToggle = maxQty <= 1;
                                return (
                                  <div
                                    key={a.id}
                                    className={cn(
                                      "rounded-xl border border-border p-3 flex items-center justify-between gap-3",
                                      qty > 0 ? "bg-accent/30" : "bg-background",
                                    )}
                                  >
                                    <div className="min-w-0">
                                      <div className="text-sm font-medium text-foreground truncate">{a.name}</div>
                                      <div className="text-xs text-muted-foreground mt-0.5">
                                        + {money(currency, Number(a.price_amount || 0))}
                                        {maxQty > 1 ? ` • max ${maxQty}` : ""}
                                      </div>
                                    </div>

                                    {isToggle ? (
                                      <Button
                                        type="button"
                                        variant={qty > 0 ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setDraftAddonQty(a.id, qty > 0 ? 0 : 1)}
                                      >
                                        {qty > 0 ? "Added" : "Add"}
                                      </Button>
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => setDraftAddonQty(a.id, Math.max(0, qty - 1))}
                                          aria-label={`Decrease ${a.name}`}
                                        >
                                          <Minus className="h-4 w-4" />
                                        </Button>
                                        <div className="w-7 text-center text-sm font-semibold">{qty}</div>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => setDraftAddonQty(a.id, Math.min(maxQty, qty + 1))}
                                          aria-label={`Increase ${a.name}`}
                                        >
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="rounded-xl border border-border bg-muted/30 p-3 text-sm flex items-center justify-between">
                            <span className="text-muted-foreground">Extras total (per item)</span>
                            <span className="font-semibold text-foreground">{money(currency, draftAddonUnitTotal)}</span>
                          </div>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                          <Label className="text-sm">Notes (optional)</Label>
                          <Textarea
                            className="min-h-[90px]"
                            value={draftNotes}
                            onChange={(e) => setDraftNotes(e.target.value)}
                            placeholder="e.g., no spicy, extra sauce"
                          />
                        </div>

                        {/* Quantity */}
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-semibold text-foreground">Quantity</div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 rounded-full"
                              onClick={() => setDraftQty((q) => Math.max(0, Number(q || 0) - 1))}
                              disabled={Number(draftQty || 0) <= 0}
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <div className="w-10 text-center text-lg font-semibold">{Math.max(0, Number(draftQty || 0))}</div>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 rounded-full"
                              onClick={() => setDraftQty((q) => Math.max(0, Number(q || 0) + 1))}
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 text-sm text-muted-foreground">Select an item to customize.</div>
                  )}
                </div>

                <div className="border-t border-border bg-background p-4">
                  {focusedItem ? (
                    <Button
                      type="button"
                      className="w-full rounded-2xl h-12"
                      onClick={applyItemSheet}
                    >
                      {Number(draftQty || 0) <= 0
                        ? "Remove from basket"
                        : `Add • ${money(
                            focusedItem.currency || currency,
                            (Number(focusedItem.price_amount || 0) + draftAddonUnitTotal) * Math.max(1, Number(draftQty || 1)),
                          )}`}
                    </Button>
                  ) : (
                    <Button type="button" className="w-full rounded-2xl h-12" onClick={() => setItemSheetOpen(false)}>
                      Done
                    </Button>
                  )}
                  <div className="mt-2 text-center text-xs text-muted-foreground">
                    Extras are priced per item and multiplied by quantity.
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Stepper */}
          {step !== "success" ? (
            <div className="flex items-center justify-center gap-2 mb-8">
              {steps.map((s, i) => (
                <div key={s.key} className="flex items-center">
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold",
                      step === s.key
                        ? "bg-primary text-primary-foreground"
                        : steps.findIndex((x) => x.key === step) > i
                          ? "bg-primary/20 text-primary"
                          : "bg-muted text-muted-foreground",
                    )}
                  >
                    {i + 1}
                  </div>
                  <span className="ml-2 text-sm font-medium text-foreground hidden sm:inline">{s.label}</span>
                  {i < steps.length - 1 ? <div className="w-8 h-px bg-border mx-2" /> : null}
                </div>
              ))}
            </div>
          ) : null}

          {/* Menu header (below stepper) */}
          {step === "menu" ? (
            <div className="mb-4">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-foreground">Menu</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Select an item for details and options, or use <span className="font-medium">+</span> to add to your order.
                </div>
              </div>
              {((data as any)?.orders_multiple_menus_enabled && Array.isArray((data as any)?.menus) && (data as any).menus.length > 0) ? (
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {((data as any).menus as Array<{ id: string; name: string; slug: string; is_default?: boolean }>).map((m) => {
                    const defaultSlug = ((data as any).menus as Array<{ slug: string; is_default?: boolean }>).find((x) => x.is_default)?.slug;
                    const href = !defaultSlug || m.slug === defaultSlug ? `/order/${businessSlug}` : `/order/${businessSlug}/${m.slug}`;
                    const isCurrent = menuSlug ? m.slug === menuSlug : !!m.is_default;
                    return (
                      <Link
                        key={m.id}
                        to={href}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors",
                          isCurrent
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border text-muted-foreground hover:bg-muted/50",
                        )}
                      >
                        {m.name}
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* MENU */}
          {step === "menu" ? (
            <div className="space-y-4 pb-24">
              <div className="rounded-2xl border border-border bg-card p-4">
                {/* Category filter (kept as-is) */}
                {(categories.length > 0 || items.some((i) => !i.category_id)) ? (
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    <button
                      type="button"
                      onClick={() => setSelectedCategory("all")}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors",
                        selectedCategory === "all"
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-border text-muted-foreground",
                      )}
                    >
                      All
                    </button>

                    {items.some((i) => !i.category_id) ? (
                      <button
                        type="button"
                        onClick={() => setSelectedCategory("uncategorized")}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors",
                          selectedCategory === "uncategorized"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border text-muted-foreground",
                        )}
                      >
                        Other
                      </button>
                    ) : null}

                    {categories.map((c: any) => (
                      <button
                        key={String(c.id)}
                        type="button"
                        onClick={() => setSelectedCategory(String(c.id))}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors",
                          String(selectedCategory) === String(c.id)
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border text-muted-foreground",
                        )}
                      >
                        {String(c.name || "Category")}
                      </button>
                    ))}
                  </div>
                ) : null}

                <div className="mt-4 space-y-3">
                  {(() => {
                    const nameByCat = new Map<string, string>();
                    (categories || []).forEach((c: any) => nameByCat.set(String(c.id), String(c.name || "Category")));
                    const groupKey = (it: any) => (it.category_id ? String(it.category_id) : "uncategorized");

                    const renderRow = (it: any) => {
                      const v = cart[it.id] || { quantity: 0, notes: "", addons: {} as Record<string, number> };
                      const qty = Math.max(0, Number(v.quantity || 0));
                      const addonQtyById = (v.addons || {}) as Record<string, number>;
                      const addonsUnit = addonUnitTotalFor(it.id, addonQtyById);
                      const hasExtrasSelected = Object.values(addonQtyById).some((n) => Number(n || 0) > 0);

                      return (
                        <div
                          key={it.id}
                          role="button"
                          tabIndex={0}
                          className={cn(
                            "w-full text-left rounded-2xl border border-border bg-background p-3 sm:p-4",
                            "hover:bg-muted/20 transition-colors cursor-pointer",
                          )}
                          onClick={() => openItemSheet(it.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openItemSheet(it.id);
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="min-w-0">
                                <div className="font-semibold text-foreground line-clamp-1">
                                  {String(it.name || "Item")}
                                </div>
                                {it.description ? (
                                  <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                                    {String(it.description)}
                                  </div>
                                ) : null}
                                <div className="mt-3 flex items-center gap-2">
                                  <div className="text-base font-semibold text-foreground whitespace-nowrap">
                                    {money(it.currency || currency, Number(it.price_amount || 0))}
                                  </div>
                                  {hasExtrasSelected ? (
                                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                                      +{money(currency, addonsUnit)} extras
                                    </div>
                                  ) : null}
                                </div>
                              </div>

                              {qty > 0 ? (
                                <div className="mt-2 text-xs text-muted-foreground">
                                  In basket: <span className="font-medium text-foreground">{qty}</span>
                                </div>
                              ) : null}
                            </div>

                            <div className="relative shrink-0">
                              {(() => {
                                const m = mediaForItem(it);
                                if (!m?.url) {
                                  return (
                                    <div className="h-24 w-24 rounded-2xl border border-border bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground flex-shrink-0">
                                      {String(it.name || "M").slice(0, 1).toUpperCase()}
                                    </div>
                                  );
                                }
                                if (m.type === "video") {
                                  return (
                                    <video
                                      src={m.url}
                                      className="h-24 w-24 rounded-2xl object-cover border border-border bg-muted flex-shrink-0"
                                      muted
                                      playsInline
                                      autoPlay
                                      loop
                                      preload="metadata"
                                    />
                                  );
                                }
                                return (
                                  <img
                                    src={m.url}
                                    alt={String(it.name || "Item")}
                                    className="h-24 w-24 rounded-2xl object-cover border border-border bg-muted flex-shrink-0"
                                    loading="lazy"
                                  />
                                );
                              })()}

                              {qty <= 0 ? (
                                <button
                                  type="button"
                                  className="absolute -bottom-2 -right-2 h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    void quickAdd(it.id);
                                  }}
                                  aria-label={`Add ${String(it.name || "item")}`}
                                >
                                  {quickAddLoadingById[it.id] ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Plus className="h-5 w-5" />
                                  )}
                                </button>
                              ) : (
                                <div
                                  className="absolute -bottom-2 -right-2 flex items-center overflow-hidden rounded-full border border-border bg-background shadow-lg"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="h-10 w-10 flex items-center justify-center"
                                    onClick={() => setQty(it.id, qty - 1)}
                                    aria-label={`Decrease ${String(it.name || "item")}`}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                  <div className="w-9 text-center text-sm font-semibold">{qty}</div>
                                  <button
                                    type="button"
                                    className="h-10 w-10 flex items-center justify-center"
                                    onClick={() => setQty(it.id, qty + 1)}
                                    aria-label={`Increase ${String(it.name || "item")}`}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    };

                    if (selectedCategory !== "all") {
                      return displayItems.map(renderRow);
                    }

                    const groups = new Map<string, any[]>();
                    (displayItems || []).forEach((it: any) => {
                      const k = groupKey(it);
                      groups.set(k, [...(groups.get(k) || []), it]);
                    });

                    const orderedKeys: string[] = [
                      ...categories.map((c: any) => String(c.id)),
                      ...(items.some((i) => !i.category_id) ? ["uncategorized"] : []),
                    ].filter((k, idx, arr) => arr.indexOf(k) === idx);

                    return orderedKeys
                      .filter((k) => (groups.get(k) || []).length > 0)
                      .map((k) => (
                        <div key={k} className="space-y-2">
                          <div className="px-1 pt-2">
                            <div className="text-sm font-semibold text-foreground">
                              {k === "uncategorized" ? "Other" : (nameByCat.get(k) || "Category")}
                            </div>
                          </div>
                          <div className="space-y-2">{(groups.get(k) || []).map(renderRow)}</div>
                        </div>
                      ));
                  })()}
                </div>
              </div>

              {/* Sticky basket bar */}
              {canContinueFromMenu ? (
                <div className="fixed bottom-4 inset-x-0 z-40 px-4 sm:px-6 lg:px-8">
                  <div className="max-w-7xl mx-auto">
                    <Button
                      type="button"
                      className="w-full h-12 rounded-2xl shadow-lg flex items-center justify-between"
                      onClick={() => {
                        setStep("details");
                        try {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        } catch {
                          window.scrollTo(0, 0);
                        }
                      }}
                    >
                      <span className="font-semibold">View basket</span>
                      <span className="text-sm font-medium">
                        {cartCount} • {money(currency, total)}
                      </span>
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* DETAILS */}
          {step === "details" ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-foreground">Your details</div>
                  <Badge variant={address.trim() ? "secondary" : "outline"} className="whitespace-nowrap">
                    {fulfillmentLabel}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <Label>Fulfillment *</Label>
                  <RadioGroup
                    value={fulfillment}
                    onValueChange={(v) => setFulfillment(v as Fulfillment)}
                    className="grid grid-cols-2 gap-2"
                  >
                    <Label
                      htmlFor="fulfillment-pickup"
                      className={cn(
                        "flex items-center gap-2 rounded-lg border border-border p-3 cursor-pointer select-none",
                        fulfillment === "pickup" ? "bg-muted/40" : "bg-background",
                      )}
                    >
                      <RadioGroupItem id="fulfillment-pickup" value="pickup" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">Pickup</div>
                        <div className="text-xs text-muted-foreground">Collect from the vendor</div>
                      </div>
                    </Label>
                    <Label
                      htmlFor="fulfillment-delivery"
                      className={cn(
                        "flex items-center gap-2 rounded-lg border border-border p-3 cursor-pointer select-none",
                        fulfillment === "delivery" ? "bg-muted/40" : "bg-background",
                      )}
                    >
                      <RadioGroupItem id="fulfillment-delivery" value="delivery" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">Delivery</div>
                        <div className="text-xs text-muted-foreground">Bring it to your location</div>
                      </div>
                    </Label>
                  </RadioGroup>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Your name *</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., Ama Mensah"
                      autoComplete="name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="e.g., 0557160977"
                      autoComplete="tel"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email (optional)</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@email.com"
                    autoComplete="email"
                  />
                </div>
                {fulfillment === "delivery" ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label>Delivery address *</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setLocationOpen(true)}
                        className="h-7 px-2 text-xs"
                      >
                        <MapPin className="h-3.5 w-3.5 mr-1" />
                        Pick on map
                      </Button>
                    </div>

                    <PlacesAutocompleteInput
                      value={address}
                      onValueChange={(v) => {
                        setAddress(v);
                        setAddressPlaceId("");
                        setAddressLat(null);
                        setAddressLng(null);
                      }}
                      onPlaceSelected={(p) => {
                        setAddress(p.formattedAddress || "");
                        setAddressPlaceId(p.placeId || "");
                        setAddressLat(typeof p.lat === "number" ? p.lat : null);
                        setAddressLng(typeof p.lng === "number" ? p.lng : null);
                      }}
                      placeholder="Start typing an address…"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use search to pick the exact location (recommended).
                    </p>
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label>Order notes (optional)</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[80px]" placeholder="Any additional instructions?" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => setStep("menu")}>
                  Back
                </Button>
                <Button onClick={() => setStep("confirm")} disabled={!canSubmit}>
                  Review order
                </Button>
              </div>
            </div>
          ) : null}

          {/* Delivery map picker */}
          <Dialog open={locationOpen && fulfillment === "delivery"} onOpenChange={setLocationOpen}>
            <DialogContent
              className="max-w-3xl max-h-[90vh] overflow-y-auto"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <DialogHeader>
                <DialogTitle>Pick delivery location</DialogTitle>
                <DialogDescription>
                  Search or drag the pin to choose the exact delivery point.
                </DialogDescription>
              </DialogHeader>
              <MapPicker
                address={pickerAddress}
                onAddressChange={(v) => {
                  setPickerAddress(v);
                  setPickerPlaceId("");
                  setPickerLat(null);
                  setPickerLng(null);
                }}
                lat={pickerLat}
                lng={pickerLng}
                onLatLngChange={(pos) => {
                  setPickerLat(pos?.lat ?? null);
                  setPickerLng(pos?.lng ?? null);
                }}
                onPlaceSelected={(p) => {
                  setPickerAddress(p.formattedAddress || "");
                  setPickerPlaceId(p.placeId || "");
                  setPickerLat(typeof p.lat === "number" ? p.lat : null);
                  setPickerLng(typeof p.lng === "number" ? p.lng : null);
                }}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setLocationOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setAddress(pickerAddress || "");
                    setAddressPlaceId(pickerPlaceId || "");
                    setAddressLat(typeof pickerLat === "number" ? pickerLat : null);
                    setAddressLng(typeof pickerLng === "number" ? pickerLng : null);
                    setLocationOpen(false);
                  }}
                >
                  Use this location
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* CONFIRM */}
          {step === "confirm" ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-foreground">Confirm your order</div>
                  <Badge variant={address.trim() ? "secondary" : "outline"} className="whitespace-nowrap">
                    {fulfillmentLabel}
                  </Badge>
                </div>
                <div className="mt-3 space-y-2">
                  {cartLines.map((l) => (
                    <div key={l.item.id} className="flex items-start justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <div className="font-medium text-foreground">
                          {l.quantity}× {l.item.name}
                        </div>
                        {l.notes ? <div className="text-xs text-muted-foreground mt-0.5">Note: {l.notes}</div> : null}
                      </div>
                      <div className="font-semibold">
                        {money(l.item.currency || currency, Number(l.item.price_amount || 0) * l.quantity)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <div className="text-sm font-medium">Total</div>
                  <div className="text-sm font-semibold">{money(currency, total)}</div>
                </div>
              </div>

              {(businessOrderPolicy.trim().length > 0 || businessTerms.trim().length > 0) ? (
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <div className="text-sm font-semibold text-foreground">Policies</div>
                  {businessOrderPolicy.trim().length > 0 ? (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-foreground">Order policy</div>
                      <div className="text-xs text-muted-foreground whitespace-pre-wrap">{businessOrderPolicy}</div>
                    </div>
                  ) : null}
                  {businessTerms.trim().length > 0 ? (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-foreground">Terms &amp; conditions</div>
                      <div className="text-xs text-muted-foreground whitespace-pre-wrap">{businessTerms}</div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => setStep("details")}>
                  Back
                </Button>
                <Button onClick={submit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Place order"
                  )}
                </Button>
              </div>
            </div>
          ) : null}

          {/* SUCCESS */}
          {step === "success" ? (
            <div className="rounded-xl border border-border bg-card p-6 text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Check className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mt-4 text-xl font-semibold text-foreground">Order received</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {(() => {
                  const raw = name.trim();
                  const safe = raw && !raw.includes("@") ? raw : "there";
                  return `Thanks, ${safe}.`;
                })()}
              </p>
              {confirmationCode ? (
                <div className="mt-4 text-sm">
                  <span className="text-muted-foreground">Reference code:</span>{" "}
                  <span className="font-semibold text-foreground">{confirmationCode}</span>
                </div>
              ) : null}
              {(() => {
                const instructions = String(
                  (data as any)?.food_order_payment_instructions ||
                    (data as any)?.booking_deposit_payment_instructions ||
                    ""
                ).trim();
                if (!instructions) return null;
                return (
                  <div className="mt-6 rounded-lg border border-border bg-accent/30 p-4 text-left">
                    <p className="text-xs text-muted-foreground">Payment instructions</p>
                    <p className="mt-1 text-sm text-foreground whitespace-pre-wrap break-words">{instructions}</p>
                  </div>
                );
              })()}
              {(() => {
                const vendorAddress = String(data?.location_address || "").trim();
                const vendorPlaceId = String(data?.location_place_id || "").trim();
                const vendorLatRaw = data?.location_lat;
                const vendorLngRaw = data?.location_lng;
                const vendorLat = vendorLatRaw != null ? Number(vendorLatRaw) : null;
                const vendorLng = vendorLngRaw != null ? Number(vendorLngRaw) : null;
                const hasLatLng =
                  vendorLat != null && vendorLng != null && !Number.isNaN(vendorLat) && !Number.isNaN(vendorLng);
                const url = mapsUrl({
                  placeId: vendorPlaceId || null,
                  address: vendorAddress || null,
                  lat: hasLatLng ? vendorLat : null,
                  lng: hasLatLng ? vendorLng : null,
                });

                if (!vendorAddress && !url && !hasLatLng) return null;

                return (
                  <div className="mt-6 rounded-lg border border-border bg-accent/30 p-4 text-left">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">Vendor location</p>
                        {vendorAddress ? (
                          <p className="text-sm font-medium text-foreground break-words">{vendorAddress}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Location available on map</p>
                        )}
                      </div>
                      {url ? (
                        <a
                          className="text-sm text-primary hover:underline whitespace-nowrap"
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Directions
                        </a>
                      ) : null}
                    </div>
                    {vendorPlaceId || vendorAddress || hasLatLng ? (
                      <MapPreview
                        lat={hasLatLng ? vendorLat : null}
                        lng={hasLatLng ? vendorLng : null}
                        placeId={vendorPlaceId || null}
                        address={vendorAddress || null}
                        className="mt-3 h-[180px] w-full rounded-lg border border-border overflow-hidden"
                      />
                    ) : null}
                  </div>
                );
              })()}
              <div className="mt-6">
                <Button variant="outline" onClick={resetToStart}>
                  Order again
                </Button>
              </div>
            </div>
          ) : null}
      </main>
    </div>
  );
}

